import { useNavigate } from "react-router-dom";
import { AlertTriangle, ShieldAlert, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { OnboardingChecklistItem } from "@/hooks/useStaffOnboarding";

interface DoctorComplianceDialogProps {
  open: boolean;
  incompleteSteps: OnboardingChecklistItem[];
  dismissCount: number;
  onDismiss: () => void;
}

export const DoctorComplianceDialog = ({
  open,
  incompleteSteps,
  dismissCount,
  onDismiss,
}: DoctorComplianceDialogProps) => {
  const navigate = useNavigate();
  // Never fully block - allow dismiss but show stronger messaging
  const isUrgent = dismissCount >= 3;
  const remainingReminders = Math.max(0, 3 - dismissCount);

  const handleCompleteProfile = () => {
    navigate("/profile");
  };

  return (
    <Dialog open={open} onOpenChange={onDismiss}>
      <DialogContent
        className="sm:max-w-md"
      >
        <DialogHeader>
          {isUrgent ? (
            <>
              <div className="flex justify-center mb-2">
                <ShieldAlert className="h-10 w-10 text-destructive" />
              </div>
              <DialogTitle className="text-center text-destructive">
                Action Required: Complete Your Profile
              </DialogTitle>
              <DialogDescription className="text-center">
                Your profile is incomplete. Some features may be limited until
                you complete the mandatory information below.
              </DialogDescription>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-2">
                <AlertTriangle className="h-10 w-10 text-yellow-500" />
              </div>
              <DialogTitle className="text-center">
                Complete Your Profile
              </DialogTitle>
              <DialogDescription className="text-center">
                The following mandatory items are still pending. Please complete
                them as soon as possible.
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        <div className="space-y-2 my-2 max-h-48 overflow-y-auto">
          {incompleteSteps.map((step) => (
            <div
              key={step.id}
              className="flex items-center gap-2 text-sm p-2 rounded-md bg-muted/50"
            >
              <CheckCircle2 className="h-4 w-4 text-muted-foreground/40 flex-shrink-0" />
              <span className="text-muted-foreground">{step.step_name}</span>
            </div>
          ))}
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button onClick={handleCompleteProfile} className="w-full">
            Complete My Profile
          </Button>
          <Button
            variant={isUrgent ? "ghost" : "outline"}
            onClick={onDismiss}
            className="w-full"
          >
            {isUrgent ? "I'll do it later" : `Remind Me Later (${remainingReminders} ${remainingReminders === 1 ? "reminder" : "reminders"} left)`}
          </Button>
          {isUrgent && (
            <p className="text-xs text-muted-foreground text-center mt-1">
              Some features may be restricted until your profile is complete.
            </p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
